import CameraAltOutlinedIcon from "@mui/icons-material/CameraAltOutlined";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import {
	Box,
	CardMedia,
	Grid2,
	IconButton,
	Link,
	SvgIcon,
	Typography
} from "@mui/material";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { zoullooColors } from "../../theme";
import { convertFileToBase64 } from "../../utils/file";

export interface ProfilePictureValue {
	id?: number;
	name?: string;
	urlPath?: string;
	content?: string;
	freelancerId?: number;
}

export interface ProfilePictureProps {
	value?: ProfilePictureValue | null;
	onChange: (photo: ProfilePictureValue | null) => void;
	onBlur?: () => void;
	name?: string;
	size?: number;
	showValidation?: boolean;
	showHelp?: boolean;
	showExamples?: boolean;
	error?: boolean;
	helperText?: string;
	disabled?: boolean;
	getPhotoUrl?: (photo: ProfilePictureValue) => string;
	setProfilePictureValidationState?: React.Dispatch<
		React.SetStateAction<ProfilePictureValidationState | null>
	>;
	profilePictureValidationState?: ProfilePictureValidationState | null;
}

export interface ProfilePictureValidationState {
	format: boolean;
	dimensions: boolean;
	size: boolean;
}

// Reusable component for photo example boxes
const PhotoExampleBox: React.FC<{ index: number; size: number }> = ({ index, size }) => {
	const { t } = useTranslation();

	const photoExampleBoxStyles = {
		width: size * 0.8,
		height: size * 0.8,
		backgroundColor: "#f0f0f0",
		borderRadius: 1,
		display: "flex",
		alignItems: "center",
		justifyContent: "center"
	};

	return (
		<Grid2 size={{ xs: 4 }}>
			<Box sx={photoExampleBoxStyles}>
				<Typography variant="body2" color="textSecondary">
					{t("profilePicture.examplePhoto", { number: index })}
				</Typography>
			</Box>
		</Grid2>
	);
};

const ProfilePicture: React.FC<ProfilePictureProps> = ({
	value,
	onChange,
	onBlur,
	name,
	size = 250,
	showValidation = true,
	showHelp = true,
	showExamples = true,
	error = false,
	helperText,
	disabled = false,
	getPhotoUrl,
	setProfilePictureValidationState,
	profilePictureValidationState
}) => {
	const { t } = useTranslation();
	const [photoExamplesOpen, setPhotoExamplesOpen] = useState(false);
	const [validationState, setValidationState] = useState<ProfilePictureValidationState>({
		format: true,
		dimensions: true,
		size: true
	});

	const defaultGetPhotoUrl = (photo: ProfilePictureValue): string => {
		if (photo.content) {
			return photo.urlPath || "";
		}

		const baseUrl = process.env.REACT_APP_ZOULLOO_FILE_STORAGE_BASE_URL;
		const containerName = process.env.REACT_APP_FREELANCER_PICTURES_CONTAINER_NAME;

		return `${baseUrl}/${containerName}/${photo.urlPath}`;
	};

	const getProfilePictureUrl = getPhotoUrl || defaultGetPhotoUrl;

	const validateFile = (file: File): ProfilePictureValidationState => {
		const newValidationState: ProfilePictureValidationState = {
			format: true,
			dimensions: true,
			size: true
		};

		// Validate file format
		const allowedFormats: { [key: string]: string[] } = {
			["image/jpeg"]: [".jpg", ".jpeg", ".jfif"],
			["image/jpg"]: [".jpg"],
			["image/png"]: [".png"],
			["image/webp"]: [".webp"]
		};

		if (
			!allowedFormats[file.type]?.includes(file.name.slice(-4).toLowerCase()) &&
			!allowedFormats[file.type]?.includes(file.name.slice(-5).toLowerCase())
		) {
			newValidationState.format = false;
		}

		// Validate file size (5MB max)
		if (file.size > 5242880) {
			newValidationState.size = false;
		}

		return newValidationState;
	};

	const processValidFile = async (file: File): Promise<void> => {
		try {
			const base64Content = await convertFileToBase64(file);
			const photoValue: ProfilePictureValue = {
				name: file.name,
				urlPath: URL.createObjectURL(file),
				content: base64Content
			};
			onChange(photoValue);
		} catch (error) {
			console.error("Error converting file to base64:", error);
		}
	};

	const validateImageDimensions = (
		file: File,
		validationState: ProfilePictureValidationState
	): void => {
		const img = new Image();
		img.onload = async () => {
			if (img.width < 400 || img.height < 400) {
				const newState = { ...validationState, dimensions: false };

				setValidationState(newState);
				onChange(null);

				return;
			}

			// If all validations pass, process the file
			await processValidFile(file);
		};

		img.src = URL.createObjectURL(file);
	};

	// Handle photo upload
	const handlePhotoUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
		const file = event.target.files?.[0];

		validateProfilePicture(file!);
	};

	const validateProfilePicture = (file: File) => {
		// Reset validation state
		setValidationState({
			format: true,
			dimensions: true,
			size: true
		});

		setProfilePictureValidationState?.(null);

		if (file) {
			const validation = validateFile(file);

			setValidationState(validation);

			if (!validation.format || !validation.size) {
				onChange(null);
				return;
			}

			// Validate dimensions separately as it requires image loading
			validateImageDimensions(file, validation);
		}
	};

	// Handle photo removal
	const handleRemovePhoto = () => {
		onChange(null);
		setValidationState({
			format: true,
			dimensions: true,
			size: true
		});
		setProfilePictureValidationState?.(null);
	};

	const hasValidationErrors =
		!(profilePictureValidationState?.format ?? true) ||
		!validationState.format ||
		!(profilePictureValidationState?.dimensions ?? true) ||
		!validationState.dimensions ||
		!(profilePictureValidationState?.size ?? true) ||
		!validationState.size;

	const renderPhotoUploadArea = () => {
		if (value) {
			return (
				<>
					{!disabled && (
						<IconButton
							onClick={e => {
								e.preventDefault();
								e.stopPropagation();
								handleRemovePhoto();
							}}
							sx={{
								position: "absolute",
								top: 0,
								right: size - 32,
								backgroundColor: "rgba(0, 0, 0, 0.6)",
								color: "white",
								width: 32,
								height: 32,
								"&:hover": {
									backgroundColor: "rgba(0, 0, 0, 0.8)"
								}
							}}
							size="small"
						>
							<CloseIcon fontSize="small" />
						</IconButton>
					)}
					<Box
						component="img"
						src={getProfilePictureUrl(value)}
						alt="Profile"
						sx={{
							width: size,
							height: size,
							borderRadius: "50%",
							objectFit: "cover",
							border: `3px ${
								error || hasValidationErrors ? "dashed #f44336" : "solid #e0e0e0"
							}`,
							...(disabled
								? {}
								: {
										"&:hover": {
											opacity: 0.8,
											border: `3px ${
												error || hasValidationErrors ? "dashed #f81101ff" : "solid #999"
											}`
										}
								  })
						}}
					/>
				</>
			);
		}

		return (
			<CardMedia
				sx={{
					width: size,
					height: size,
					borderRadius: "50%",
					border: `2px dashed ${error || hasValidationErrors ? "#f44336" : "#ccc"}`,
					display: "flex",
					flexDirection: "column",
					alignItems: "center",
					justifyContent: "center",
					gap: 1,
					...(disabled
						? {}
						: {
								"&:hover": {
									backgroundColor: "#e8e8e8",
									border: `2px dashed ${
										error || hasValidationErrors ? "#f81101ff" : "#999"
									}`,
									"& > svg": {
										stroke: "#e8e8e8"
									}
								}
						  })
				}}
				image="/assets/images/placeholder-profile-icon.png"
			>
				<SvgIcon
					component={CameraAltOutlinedIcon}
					sx={{
						fontSize: size * 0.25,
						color: zoullooColors.grey[500]
					}}
					strokeWidth={1}
					stroke={zoullooColors.background.paper}
				/>
				<Typography
					variant="body1"
					sx={{
						fontSize: size * 0.08,
						color: zoullooColors.grey[500],
						textAlign: "center",
						px: 2
					}}
				>
					{t("profilePicture.uploadPhoto")}
				</Typography>
			</CardMedia>
		);
	};

	const renderValidationMessages = () => {
		if (!showValidation) return null;

		return (
			<Box sx={{ mt: 2, width: "100%" }}>
				<Typography
					variant="body2"
					sx={{
						mb: 1,
						fontWeight: 500,
						color:
							profilePictureValidationState?.format ?? validationState.format
								? "text.primary"
								: "error.main"
					}}
				>
					{t("profilePicture.validation.photoFormat")}
				</Typography>
				<Typography
					variant="body2"
					sx={{
						mb: 1,
						fontWeight: 500,
						color:
							profilePictureValidationState?.dimensions ?? validationState.dimensions
								? "text.primary"
								: "error.main"
					}}
				>
					{t("profilePicture.validation.photoDimensions")}
				</Typography>
				<Typography
					variant="body2"
					sx={{
						mb: 1,
						fontWeight: 500,
						color:
							profilePictureValidationState?.size ?? validationState.size
								? "text.primary"
								: "error.main"
					}}
				>
					{t("profilePicture.validation.photoSize")}
				</Typography>
			</Box>
		);
	};

	const renderPhotoExamples = () => {
		if (!showExamples || !photoExamplesOpen) return null;

		return (
			<Box sx={{ mt: 4 }}>
				<Typography
					variant="h6"
					sx={{
						mb: 2,
						display: "flex",
						alignItems: "center",
						color: "success.main"
					}}
				>
					<CheckIcon sx={{ mr: 1 }} />
					{t("profilePicture.recommendedPhotos")}
				</Typography>
				<Grid2 container spacing={2} sx={{ mb: 3 }}>
					{[1, 2, 3].map(index => (
						<PhotoExampleBox key={`good-${index}`} index={index} size={size} />
					))}
				</Grid2>

				<Typography
					variant="h6"
					sx={{
						mb: 2,
						display: "flex",
						alignItems: "center",
						color: "error.main"
					}}
				>
					<CloseIcon sx={{ mr: 1 }} />
					{t("profilePicture.notRecommendedPhotos")}
				</Typography>
				<Grid2 container spacing={2}>
					{[1, 2, 3].map(index => (
						<PhotoExampleBox key={`bad-${index}`} index={index} size={size} />
					))}
				</Grid2>
			</Box>
		);
	};

	return (
		<Grid2 container spacing={4} sx={{ alignItems: "flex-start", my: 10 }}>
			{/* Left side - Photo upload area */}
			<Grid2 size={{ xs: 12, md: showHelp ? 5 : 12 }}>
				<Box
					sx={{
						display: "flex",
						flexDirection: "column",
						alignItems: showHelp ? "center" : "flex-start",
						gap: 2
					}}
				>
					<Box
						component="label"
						sx={{
							cursor: disabled ? "default" : "pointer",
							display: "block",
							position: "relative"
						}}
					>
						{renderPhotoUploadArea()}
						<input
							type="file"
							hidden
							accept={".png, .jpg, .jpeg, .jfif, .webp"}
							onChange={handlePhotoUpload}
							onBlur={onBlur}
							name={name}
							disabled={disabled}
							aria-label="Upload profile photo"
						/>
					</Box>

					{/* Validation error display */}
					{(error || hasValidationErrors) && helperText && (
						<Typography variant="body2" color="error" sx={{ textAlign: "center", mt: 1 }}>
							{helperText}
						</Typography>
					)}

					{/* Photo validation display */}
					{renderValidationMessages()}
				</Box>
			</Grid2>

			{/* Right side - Help text and tips */}
			{showHelp && (
				<Grid2 size={{ xs: 12, md: 7 }}>
					<Box sx={{ pl: { md: 2 } }}>
						<Typography variant="h5" sx={{ mb: 2 }}>
							{t("profilePicture.photoHelp")}
						</Typography>

						<Box component="ul" sx={{ pl: 2, mb: 2 }}>
							{(
								t("profilePicture.photoTips", {
									returnObjects: true
								}) as string[]
							).map(tip => (
								<Typography
									variant="body1"
									component="li"
									key={tip.substring(0, 20)}
									sx={{ mb: 1, color: "text.secondary" }}
								>
									{tip}
								</Typography>
							))}
						</Box>

						{showExamples && (
							<>
								<Link
									href="#"
									color="secondary"
									sx={{
										textDecoration: "underline",
										fontSize: "1rem",
										fontWeight: 600,
										cursor: "pointer",
										display: "flex",
										alignItems: "center",
										gap: 1,
										right: "8px",
										position: "relative"
									}}
									onClick={e => {
										e.preventDefault();
										setPhotoExamplesOpen(!photoExamplesOpen);
									}}
								>
									{photoExamplesOpen ? (
										<KeyboardArrowDownIcon fontSize="medium" sx={{ fontWeight: 600 }} />
									) : (
										<KeyboardArrowRightIcon fontSize="medium" sx={{ fontWeight: 600 }} />
									)}
									{t("profilePicture.viewPhotoExamples")}
								</Link>

								{/* Collapsible Recommended/Not Recommended Photos Section */}
								{renderPhotoExamples()}
							</>
						)}
					</Box>
				</Grid2>
			)}
		</Grid2>
	);
};

export default ProfilePicture;
